import type { ThunkAction, UnknownAction } from "@reduxjs/toolkit";

import {
	SetProductionYearPlanCellsInput,
	SetProductionYearPlanItemInput,
	SetProductionYearPlanProductionPlanInput
} from "~/gql/graphqlBase.generated";
import {
	SetProductionYearPlanCellsMutation,
	SetProductionYearPlanItemMutation,
	SetProductionYearPlanProductionPlanMutation
} from "~/gql/mutations/produce/productionYearPlan.generated";
import {
	GetProductionYearPlanQuery,
	GetProductionYearPlanQueryVariables,
	GetProductionYearPlanYearsQuery
} from "~/gql/queries/produce/productionYearPlan.generated";

const importApiEndpointsLazilyToBreakCycle = async () =>
	(await import("~/services/endpoints/apiEndpoints")).apiEndpoints;

type CycleFreeLifecycleDispatch = <ReturnValue, State>(
	thunkAction: ThunkAction<ReturnValue, State, unknown, UnknownAction>
) => ReturnValue;

interface ProductionYearPlanLifecycle<TMutation> {
	dispatch: CycleFreeLifecycleDispatch;
	queryFulfilled: Promise<{ data: TMutation }>;
}

const storeSavedPlan = async <TMutation>(
	{ dispatch, queryFulfilled }: ProductionYearPlanLifecycle<TMutation>,
	{ isoYear, siteLocationId }: GetProductionYearPlanQueryVariables,
	selectSavedPlan: (data: TMutation) => GetProductionYearPlanQuery["productionYearPlan"]
) => {
	// Resolve the import first so the plan is cached before the mutation's caller resumes.
	const apiEndpoints = await importApiEndpointsLazilyToBreakCycle();
	const fulfilled = await queryFulfilled.catch(() => null);
	const savedPlan = fulfilled && selectSavedPlan(fulfilled.data);
	if (!savedPlan) return;
	dispatch(
		apiEndpoints.util.updateQueryData("GetProductionYearPlan", { isoYear, siteLocationId }, (draft) => {
			draft.productionYearPlan = savedPlan;
		})
	);
};

const productionYearPlanEndpoints = {
	GetProductionYearPlan: {
		providesTags: ["ProductionYearPlans"] as AddTagTypes,
		transformResponse: (res: GetProductionYearPlanQuery) => res
	},
	GetProductionYearPlanYears: {
		providesTags: ["ProductionYearPlanYears"] as AddTagTypes,
		transformResponse: (res: GetProductionYearPlanYearsQuery) => res
	},
	SetProductionYearPlanCells: {
		invalidatesTags: ["ProductionYearPlanYears"] as AddTagTypes,
		async onQueryStarted(
			arg: { input: SetProductionYearPlanCellsInput },
			lifecycle: ProductionYearPlanLifecycle<SetProductionYearPlanCellsMutation>
		) {
			await storeSavedPlan(lifecycle, arg.input, (data) => data.setProductionYearPlanCells.productionYearPlan);
		},
		transformResponse: (res: SetProductionYearPlanCellsMutation) => res
	},
	SetProductionYearPlanItem: {
		invalidatesTags: ["ProductionYearPlanYears"] as AddTagTypes,
		async onQueryStarted(
			arg: { input: SetProductionYearPlanItemInput },
			lifecycle: ProductionYearPlanLifecycle<SetProductionYearPlanItemMutation>
		) {
			await storeSavedPlan(lifecycle, arg.input, (data) => data.setProductionYearPlanItem.productionYearPlan);
		},
		transformResponse: (res: SetProductionYearPlanItemMutation) => res
	},
	SetProductionYearPlanProductionPlan: {
		invalidatesTags: ["ProductionYearPlanYears"] as AddTagTypes,
		async onQueryStarted(
			arg: { input: SetProductionYearPlanProductionPlanInput },
			lifecycle: ProductionYearPlanLifecycle<SetProductionYearPlanProductionPlanMutation>
		) {
			await storeSavedPlan(lifecycle, arg.input, (data) => data.setProductionYearPlanProductionPlan.productionYearPlan);
		},
		transformResponse: (res: SetProductionYearPlanProductionPlanMutation) => res
	}
};

export default productionYearPlanEndpoints;
